// v2.16.2 — Page publique JobDetail (/jobs/:id) avec détail complet d'une offre.

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api";
const ACCENT = "#10B981";

const CONTRACT_COLORS = {
  CDI: "#16A34A", CDD: "#F59E0B", Stage: "#3B82F6", Freelance: "#7C3AED", Apprentissage: "#06B6D4",
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtFCFA(n) { return new Intl.NumberFormat("fr-FR").format(n) + " FCFA"; }

export default function JobDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/v3/emploi/${id}`)
      .then((r) => { if (!cancelled) { setItem(r.data?.item || null); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Offre introuvable ou expirée"); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  const apply = () => {
    if (!item?.apply_url) return;
    window.open(item.apply_url, "_blank", "noopener,noreferrer");
  };
  const copyOffer = async () => {
    if (!item) return;
    const text = `💼 ${item.title}\n🏢 ${item.company}\n📍 ${item.location_text}\n💰 ${item.salary_display}\n📋 ${item.contract_type} · ${item.experience_level}\n\n🔗 Postuler : ${item.apply_url}\nSource : ${item.platform_name}`;
    try { await navigator.clipboard.writeText(text); toast.success("📋 Offre copiée"); }
    catch (_) { toast.error("Copie impossible"); }
  };
  const share = () => {
    if (!item) return;
    const msg = `💼 ${item.title} chez ${item.company} (${item.location_text}, ${item.contract_type})\n💰 ${item.salary_display}\n\n🔗 Postuler : ${item.apply_url}\n\nVu sur CIV-AI 🇨🇮`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) return <PageShell><div data-testid="job-detail-loading" style={{ color: "#cbd5e1", padding: 40 }}>Chargement de l'offre…</div></PageShell>;
  if (error || !item) return <PageShell>
    <div data-testid="job-detail-error" style={{ color: "#fda4af", padding: 40, textAlign: "center" }}>
      ⚠️ {error || "Offre introuvable"}
      <div style={{ marginTop: 16 }}>
        <button onClick={() => nav("/")} style={btnSecondary}>← Retour à CIV-AI</button>
      </div>
    </div>
  </PageShell>;

  const contractBg = CONTRACT_COLORS[item.contract_type] || "#64748B";

  return (
    <PageShell>
      <Toaster richColors position="top-center" />
      <div data-testid="job-detail-page" style={{
        maxWidth: 820, margin: "0 auto", padding: "32px 20px",
        color: "#F1F5F9", fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        <button onClick={() => nav(-1)} data-testid="job-detail-back" style={{
          background: "transparent", border: "1px solid rgba(255,255,255,0.15)",
          color: "#cbd5e1", padding: "6px 14px", borderRadius: 999,
          fontSize: 12.5, cursor: "pointer", marginBottom: 22,
        }}>← Retour</button>

        {/* Bandeau */}
        <div style={{
          background: `linear-gradient(135deg, ${ACCENT}40 0%, ${ACCENT}10 100%)`,
          border: `1px solid ${ACCENT}55`, borderRadius: 16, padding: "20px 22px", marginBottom: 22,
        }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ background: "rgba(15,23,42,0.85)", color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
              📰 {item.platform_name}
            </span>
            <span style={{ background: contractBg, color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 800 }}>
              {item.contract_type}
            </span>
            <span style={{ background: "rgba(255,255,255,0.08)", color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: 11 }}>
              {item.category_emoji} {item.category_label}
            </span>
            {item.verified && (
              <span style={{ background: "#0EA5E9", color: "#fff", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>✓ Vérifiée</span>
            )}
          </div>
          <h1 data-testid="job-detail-title" style={{ fontSize: 26, margin: "4px 0 6px", lineHeight: 1.15 }}>{item.title}</h1>
          <div style={{ fontSize: 14, color: "#cbd5e1" }}>🏢 <strong>{item.company}</strong></div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>📍 {item.location_text}</div>
        </div>

        {/* CTA principal */}
        <button data-testid="job-detail-apply-btn" onClick={apply} style={{
          width: "100%", background: ACCENT, color: "#fff", border: "none",
          padding: "14px 18px", borderRadius: 12, cursor: "pointer",
          fontWeight: 800, fontSize: 15, marginBottom: 14,
          boxShadow: `0 4px 14px ${ACCENT}55`,
        }}>🔗 Postuler sur {item.platform_name}</button>

        <div style={{ display: "flex", gap: 8, marginBottom: 22, flexWrap: "wrap" }}>
          <button onClick={copyOffer} data-testid="job-detail-copy-btn" style={btnSecondary}>📋 Copier l'offre</button>
          <button onClick={share} data-testid="job-detail-share-btn" style={{ ...btnSecondary, background: "#25D366", color: "#fff", borderColor: "transparent" }}>📤 Partager WhatsApp</button>
        </div>

        {/* Sections */}
        <Block icon="📋" title="Détails">
          <Row k="Type de contrat" v={item.contract_type} />
          <Row k="Niveau d'expérience" v={item.experience_level} />
          <Row k="Rémunération" v={`${fmtFCFA(item.salary_fcfa_min)} – ${fmtFCFA(item.salary_fcfa_max)} / mois`} />
          <Row k="Localisation" v={item.location_text} />
        </Block>

        <Block icon="📝" title="Description">
          <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 13.5, color: "#cbd5e1", lineHeight: 1.65, margin: 0 }}>
            {item.description_full}
          </pre>
        </Block>

        {Array.isArray(item.skills) && item.skills.length > 0 && (
          <Block icon="🛠️" title="Compétences attendues">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {item.skills.map((s) => (
                <span key={s} style={{
                  background: `${ACCENT}1A`, border: `1px solid ${ACCENT}55`,
                  color: "#fff", padding: "4px 10px", borderRadius: 999,
                  fontSize: 12, fontWeight: 600,
                }}>{s}</span>
              ))}
            </div>
          </Block>
        )}

        <Block icon="📅" title="Dates clés">
          <Row k="Publiée le" v={fmtDate(item.posted_at)} />
          <Row k="Date limite" v={fmtDate(item.deadline_at)} />
        </Block>

        <div style={{ marginTop: 28, padding: 14, background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 10, fontSize: 12, color: "#94a3b8" }}>
          ℹ️ Cette offre est agrégée depuis <a href={item.platform_url} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT }}>{item.platform_name}</a>. Les candidatures se font directement sur la plateforme.
        </div>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(180deg, #0B1220 0%, #0A1029 100%)" }}>
      <header style={{ padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", color: "#fff" }}>
        <a href="/" style={{ color: "#fff", textDecoration: "none", fontWeight: 800, fontSize: 14 }}>🇨🇮 CIV-AI · 💼 Emploi</a>
      </header>
      {children}
    </div>
  );
}
function Block({ icon, title, children }) {
  return (
    <section style={{ marginBottom: 18, padding: 16, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12 }}>
      <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700, color: "#FBBF24" }}>{icon} {title}</h3>
      {children}
    </section>
  );
}
function Row({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.05)", fontSize: 13 }}>
      <span style={{ color: "#94a3b8" }}>{k}</span>
      <span style={{ color: "#fff", fontWeight: 600 }}>{v}</span>
    </div>
  );
}
const btnSecondary = {
  background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)",
  color: "#fff", padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13,
};
